<script setup lang="ts">
/**
 * A stop of a journey timeline: its time, its circle on the rail and its name. `main` for
 * where something happens (start, boarding, alighting, arrival), `intermediate` for a stop
 * a ride calls at; `past` greys out a stop behind the traveller.
 */
withDefaults(
  defineProps<{
    time: string;
    title: string;
    detail?: string;
    type?: 'main' | 'intermediate';
    state?: 'default' | 'past';
    /** `filled` marks the arrival */
    marker?: 'ring' | 'filled';
    lineAbove?: boolean;
    lineBelow?: boolean;
    /** Color of the circle and the rail, the line's color on a ride */
    color?: string;
  }>(),
  {
    detail: undefined,
    type: 'main',
    state: 'default',
    marker: 'ring',
    lineAbove: false,
    lineBelow: false,
    color: undefined,
  },
);
</script>

<template>
  <div
    class="timeline-stop"
    :class="[`timeline-stop--${type}`, { 'timeline-stop--past': state === 'past' }]"
    :style="color ? { '--timeline-stop-color': color } : undefined"
    role="listitem"
  >
    <span class="timeline-stop__time">{{ time }}</span>
    <span class="timeline-stop__rail" aria-hidden="true">
      <span v-if="lineAbove" class="timeline-stop__line timeline-stop__line--above" />
      <span v-if="lineBelow" class="timeline-stop__line timeline-stop__line--below" />
      <span class="timeline-stop__node" :class="{ 'timeline-stop__node--filled': marker === 'filled' }" />
    </span>
    <span class="timeline-stop__content">
      <span class="timeline-stop__title">{{ title }}</span>
      <span v-if="detail" class="timeline-stop__detail">{{ detail }}</span>
    </span>
  </div>
</template>

<style lang="scss">
.timeline-stop {
  --timeline-stop-rail: var(--timeline-stop-color, #{$color-neutral-highest});

  display: flex;
  gap: $spacing-2;

  &__time {
    color: $color-neutral-highest;
    flex-shrink: 0;
    font: $label-small;
    letter-spacing: $label-small-spacing;
    text-align: right;
    white-space: nowrap;
    // A timeline sets `--timeline-time-width` to its widest time (« 12:45 PM ») to keep the rows aligned
    width: max(44px, var(--timeline-time-width, 0px));
  }

  &__rail {
    align-self: stretch;
    flex-shrink: 0;
    position: relative;
    width: 20px;
  }

  // The circle sits centred 10px down the row, over the ends of both lines
  &__line {
    background-color: var(--timeline-stop-rail);
    left: 8px;
    position: absolute;
    width: 4px;

    &--above {
      height: 6px;
      top: 0;
    }

    &--below {
      bottom: 0;
      top: 10px;
    }
  }

  &__node {
    background-color: $color-white;
    border: 3px solid var(--timeline-stop-rail);
    border-radius: 50%;
    box-sizing: border-box;
    height: 14px;
    left: 3px;
    position: absolute;
    top: 3px;
    width: 14px;

    &--filled {
      background-color: var(--timeline-stop-rail);
    }
  }

  &__content {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
    padding-bottom: 14px;
  }

  &__title {
    color: $color-neutral-highest;
    font: $label-medium;
    letter-spacing: $label-medium-spacing;
  }

  &__detail {
    color: $color-neutral-higher;
    font: $text-smaller;
    letter-spacing: $text-smaller-spacing;
  }

  &--intermediate {
    .timeline-stop__time {
      color: $color-neutral-higher;
      font: $text-smaller;
      letter-spacing: $text-smaller-spacing;
      padding-top: 2px;
    }

    .timeline-stop__node {
      border-width: 2px;
      height: 8px;
      left: 6px;
      top: 6px;
      width: 8px;
    }

    .timeline-stop__content {
      padding-bottom: 6px;
    }

    .timeline-stop__title {
      color: $color-neutral-higher;
      font: $text-small;
      letter-spacing: $text-small-spacing;
    }
  }

  &--past {
    --timeline-stop-rail: #{$color-neutral-high};

    .timeline-stop__time,
    .timeline-stop__title,
    .timeline-stop__detail {
      color: $color-neutral-high;
    }
  }
}
</style>
